import { parse } from 'graphql';

import { createClientGraphQLClient } from '../graphql/client/createClientGraphQLClient';
import type { RuntimeEnvironment } from '../graphql/config/runtimeConfig';
import type { ConsumeCrossLaunchTokenMutation } from '../graphql/queries/consumeCrossLaunchToken.generated';
import type { DashboardAuth } from './authStore';
import { normalizeDashboardAuth } from './dashboardAuth';

export type ConsumeCrossLaunchTokenClientOverrides = {
  env: RuntimeEnvironment;
  fetchImpl?: Parameters<typeof createClientGraphQLClient>[0]['fetchImpl'];
};

export class InvalidCrossLaunchAuthenticationResponseError extends Error {
  constructor() {
    super('Cross-launch authentication response was invalid.');
    this.name = 'InvalidCrossLaunchAuthenticationResponseError';
  }
}

// Keep this selection set in sync with consumeCrossLaunchToken.graphql. Codegen
// emits operation types only, so this is the runtime document sent to Core.
export const CONSUME_CROSS_LAUNCH_TOKEN_MUTATION = parse(/* GraphQL */ `
  mutation ConsumeCrossLaunchToken($token: String!) @api(name: core) {
    consumeCrossLaunchToken(token: $token) {
      authJwt
      agentId
      brokerageId
      isBroker
    }
  }
`);

// Redeems the opaque, one-time cross-launch token with an intentionally public
// Core client. It neither reads nor alters authStore, so an existing Field Ops
// session is never sent with or affected by this request.
export const consumeCrossLaunchToken = async (
  token: string,
  overrides: ConsumeCrossLaunchTokenClientOverrides,
): Promise<DashboardAuth> => {
  const client = createClientGraphQLClient(overrides);
  const result = await client.request<ConsumeCrossLaunchTokenMutation>({
    document: CONSUME_CROSS_LAUNCH_TOKEN_MUTATION,
    variables: { token },
  });
  const normalizedAuth = normalizeDashboardAuth(result.consumeCrossLaunchToken);

  if (normalizedAuth.kind === 'invalid') {
    throw new InvalidCrossLaunchAuthenticationResponseError();
  }

  return normalizedAuth.value;
};
